import Skeleton from "./Skeleton";
import SkeletonTextLine from "./SkeletonTextLine";

/**
 * The library grid's geometry, owned here and imported by the page, so the
 * silhouette and the real list cannot drift into different column counts — the
 * one way a grid skeleton fails visibly is by reflowing on hand-off.
 */
export const VISUAL_ABSTRACT_LIBRARY_GRID_CLASS =
  "m-0 grid list-none grid-cols-2 gap-3 p-0 md:grid-cols-3 md:gap-4 lg:grid-cols-4";

/**
 * The desktop shell's grid (Figma "D2.va-center.grid"): fixed 260 × 300 cards,
 * as many to a row as the content column holds, rather than a column count
 * stretched to the window.
 */
export const VISUAL_ABSTRACT_DESKTOP_LIBRARY_GRID_CLASS =
  "m-0 grid list-none grid-cols-[repeat(auto-fill,--spacing(65))] auto-rows-[--spacing(75)] gap-4 p-0";

/**
 * A library card is a SOLID brand-coloured band with white type on it, so its
 * silhouette is the band itself — not a grey card with grey bars inside, which
 * would stand in for a layout the page never paints. 208px is the card at a
 * three-line title: chip (22) + title (3 × 22) + source line (16) + the
 * generated-by lockup (44) inside `py-4`.
 */
const LIBRARY_CARD_HEIGHT_PX = 208;

export function VisualAbstractLibraryGridSkeleton({
  count,
  shell,
}: {
  count: number;
  shell: "mobile" | "desktop";
}): React.JSX.Element {
  return (
    <ul
      className={
        shell === "desktop"
          ? VISUAL_ABSTRACT_DESKTOP_LIBRARY_GRID_CLASS
          : VISUAL_ABSTRACT_LIBRARY_GRID_CLASS
      }
    >
      {Array.from({ length: count }).map((_, index) => (
        <li key={index}>
          <Skeleton
            className="rounded-2xl!"
            // The desktop row fixes the card's height; the phone card grows
            // with its title.
            height={shell === "desktop" ? "100%" : LIBRARY_CARD_HEIGHT_PX}
            width="100%"
          />
        </li>
      ))}
    </ul>
  );
}

/**
 * Cold-boot silhouette for `/visual-abstracts`, the library reached from the
 * JuisciGPT drawer. Without it the boot gate falls through to the Discovery
 * shell — a feed with a navbar, which this screen is not.
 *
 * Composes the same grid the page falls back to while its own query is pending,
 * so the hand-off from boot to page to data never moves a card. Pure render (no
 * queries, no auth reads), like every boot skeleton.
 */
export default function VisualAbstractLibraryBootSkeleton(): React.JSX.Element {
  return (
    <div className="flex h-full flex-col bg-ds-cream">
      {/* `ContentHeader` under the ScrollableLayout header slot, which owns the
          safe area and the translucent white: back chevron + label on the left,
          centered title, no actions. */}
      <div
        className="bg-white/90"
        style={{ paddingTop: "var(--safe-area-top)" }}
      >
        <div className="border-b border-secondary-soft-yellow-grey px-3.5 py-3">
          <div className="relative flex items-center">
            <div className="flex items-center gap-1">
              <Skeleton $variant="rounded" width={24} height={24} />
              <SkeletonTextLine width={38} fontSize={16} lineHeight={24} />
            </div>
            <div className="absolute left-1/2 -translate-x-1/2">
              <SkeletonTextLine width={116} fontSize={16} lineHeight={24} />
            </div>
          </div>
        </div>
      </div>

      {/* The page's own column: `max-w-6xl px-4 pt-4`. Six cards fill a phone
          viewport three rows deep without the silhouette running past it. */}
      <div className="mx-auto w-full max-w-6xl overflow-hidden px-4 pt-4">
        <VisualAbstractLibraryGridSkeleton count={6} shell="mobile" />
      </div>
    </div>
  );
}
